import {
  Navigate as RouterNavigate,
  useLocation,
  useNavigate,
  useMatch as useReactRouterMatch,
  useParams as useReactRouterParams,
} from 'react-router-dom'

import type { ExtractPathParams, RoutePath, TypedTo } from '@/router'

import type {
  RouterFactoryNavigate,
  RouterFactoryNavigateComponentProps,
  RouterFactoryUseMatch,
} from './create-router-factory.types'

/**
 * Builds a path string from a route path and optional params.
 * Replaces :param segments with values from params.
 */
const getPathImpl = <P extends RoutePath>(
  path: P,
  params?: Record<string, string | number>,
): string => {
  if (!params || Object.keys(params).length === 0) {
    return path
  }
  let result: string = path
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`:${key}`, String(value))
  }
  return result
}

/**
 * Converts a typed destination ({ path, params }) to a path string.
 * Use this for Link components or when you need the resolved path.
 */
export const getPathString = (to: TypedTo): string => {
  const params = (to as { params?: Record<string, string | number> }).params
  const path = getPathImpl(to.path as RoutePath, params)
  const queryString = to.queryParams ? new URLSearchParams(to.queryParams).toString() : ''
  return queryString ? `${path}?${queryString}` : path
}

/**
 * Typed Navigate component: accepts TypedTo ({ path, params })
 * and renders react-router's Navigate with the resolved path.
 */
const Navigate = ({ to, replace, state }: RouterFactoryNavigateComponentProps) => (
  <RouterNavigate to={getPathString(to)} replace={replace} state={state} />
)

/**
 * Hook that returns typed navigation helpers and typed Navigate component.
 */
export const useRouter = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const typedNavigate: RouterFactoryNavigate = (to) => {
    navigate(getPathString(to))
  }

  const navigateBack = (fallback: TypedTo) => {
    if (location.key !== 'default') {
      navigate(-1)
    } else {
      navigate(getPathString(fallback))
    }
  }

  const useTypedUseMatch: RouterFactoryUseMatch = (to) => {
    return useReactRouterMatch(to.path)
  }

  const useTypedUseParams: import('./create-router-factory.types').RouterFactoryUseParams = <
    P extends RoutePath,
  >() => useReactRouterParams() as unknown as Record<keyof ExtractPathParams<P>, string>

  return {
    Navigate,
    navigate: typedNavigate,
    navigateBack,
    useMatch: useTypedUseMatch,
    useParams: useTypedUseParams,
  }
}

export const createRouterFactory = () => {
  return {
    getPathString,
    useRouter,
  }
}
